import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Grid,
    Typography
} from "@mui/material";

import {
    Groups
} from "@mui/icons-material";

import ChartCard from './chartCard';
import CardSummary from '../../../shared/dashboard/cardSummary';
import CardDark from '../../../shared/general/CardDark';
import { getVendorIncomeExpenseMonthly } from '../../../../../actions/vendor';

const IncomeAndExpenses = ({ year, company }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { result: vendorContract } = useSelector(
        (state) => state.vendorContract
      );
    const { result: incomeExpense } = useSelector(
        (state) => state.vendorIncomeExpense
      );
      const rows = useMemo(() => {
        if (!vendorContract) return [];
        return company
          ? vendorContract.filter((c) => c.idCompany === company)
          : vendorContract;
      }, [vendorContract, company]);

      const sumEmployees = () => {
        return rows.reduce((accumulator, object) => {
            return accumulator + (object.numberEmployee || 0);
          }, 0);
      }

      const summary = useMemo(() => {
        return rows.reduce(
          (acc, c) => ({
            revenue: acc.revenue + (c.revenueMonth || 0),
            expense: acc.expense + (c.expensesMonth || 0),
            difference: acc.difference + (c.differenceMonth || 0),
            payWage: acc.payWage + (c.payWageMonth || 0),
            payOT: acc.payOT + (c.payOTMonth || 0),
            payOther: acc.payOther + (c.payOtherMonth || 0),
            revWage: acc.revWage + (c.revWageMonth || 0),
            revOperating: acc.revOperating + (c.revOperatingMonth || 0),
          }),
          {
            revenue: 0, expense: 0, difference: 0,
            payWage: 0, payOT: 0, payOther: 0,
            revWage: 0, revOperating: 0,
          }
        );
      }, [rows]);

      useEffect(() => {
        const already =
          incomeExpense &&
          incomeExpense.year === year &&
          incomeExpense.idCompany === (company || null);
        if (year && !already) {
          dispatch(getVendorIncomeExpenseMonthly(year, company));
        }
      }, [year, company, incomeExpense]);

      const incomeExpenseSeries = useMemo(() => {
        if (!incomeExpense) return [];
        return [
          { name: t("IE_Income"), data: incomeExpense.revenue },
          { name: t("IE_Expense"), data: incomeExpense.expense },
        ];
      }, [incomeExpense, t]);

      const operatingSeries = useMemo(() => {
        if (!incomeExpense) return [];
        return [{ name: t("IE_Operating"), data: incomeExpense.operating }];
      }, [incomeExpense, t]);

    return (
        <div style={{ height:"fit-content", minHeight:"calc(100vh - 192px)", marginTop:"20px" }}>
            <Grid container>
                <Grid container item spacing={2}>
                    <Grid item xs={12} md={8}>
                        <CardSummary 
                            value={{
                                label: t("IE_Employees"),
                                amount: sumEmployees(),
                                icon: <Groups />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <CardSummary 
                            value={{
                                label: t("IE_Partners"),
                                amount: rows.length,
                                icon: <i className="fa-solid fa-building" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_PayWage"),
                                amount: summary.payWage,
                                icon: <i className="fa-solid fa-briefcase" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_PayOT"),
                                amount: summary.payOT,
                                icon: <i className="fa-solid fa-clock" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_PayOther"),
                                amount: summary.payOther,
                                icon: <i className="fa-solid fa-box" />
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_PayTotal"),
                                amount: summary.expense,
                                icon: <i className="fa-solid fa-money-bill" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_RevenueWage"),
                                amount: summary.revWage,
                                icon: <i className="fa-solid fa-user-helmet-safety" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_RevenueOperating"),
                                amount: summary.revOperating,
                                icon: <i className="fa-solid fa-circle-dollar" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_RevenueTotal"),
                                amount: summary.revenue,
                                icon: <i className="fa-solid fa-money-bills" />,
                            }}
                            top={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <CardSummary
                            value={{
                                label: t("IE_Difference"),
                                amount: summary.difference,
                                icon: <i className="fa-solid fa-sack-dollar" />,
                            }}
                            top={true}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ paddingTop:"16px" }}>
                    <CardDark>
                        <Box style={{ width:"100%", display:"flex", flexDirection:"column", padding:"24px 24px 0" }}>
                            <Typography variant="body1" style={{ color: "#e6e5e8" }}>{t("IE_IncomeExpense")}</Typography>
                            <ChartCard data={incomeExpenseSeries} />
                        </Box>
                    </CardDark>
                </Grid>
                <Grid item xs={12} style={{ paddingTop:"16px" }}>
                    <CardDark>
                        <Box style={{ width:"100%", display:"flex", flexDirection:"column", padding:"24px 24px 0" }}>
                            <Typography variant="body1" style={{ color: "#e6e5e8" }}>{t("IE_Operating")}</Typography>
                            <ChartCard data={operatingSeries} />
                        </Box>
                    </CardDark>
                </Grid>
            </Grid>
        </div>
    );
};

export default IncomeAndExpenses;
